import React from "react";
import styled from "styled-components";
import { Card, Empty, Space, Tag, Typography } from "antd";
import DashboardDrilldownDrawer from "./DashboardDrilldownDrawer.jsx";
import useDashboardDrilldown from "../hooks/useDashboardDrilldown.js";

const { Text } = Typography;

const AlertCard = styled(Card)`
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  margin-bottom: 24px;

  .ant-card-head-title {
    color: #005249;
    font-weight: 700;
  }
`;

const AlertGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 12px;
`;

const AlertTile = styled.a`
  display: block;
  border: 1px solid ${({ $severity }) => ($severity === "critical" ? "#ffccc7" : "#ffe58f")};
  border-radius: 10px;
  padding: 12px;
  background: ${({ $severity }) => ($severity === "critical" ? "#fff2f0" : "#fffbe6")};
  color: inherit;
  cursor: pointer;

  &:hover {
    color: inherit;
    border-color: #005249;
  }
`;

const ExecutiveRiskAlertStrip = ({ metricGroups, dashboardFilters, loading = false }) => {
  const drilldownState = useDashboardDrilldown(dashboardFilters);

  if (loading) return null;

  const alerts = buildAlerts(metricGroups).slice(0, 6);

  return (
    <>
      <AlertCard
        title="Executive Risk Alerts"
        extra={alerts.length > 0 && <Tag color="red">{alerts.length} active</Tag>}
        data-testid="executive-risk-alert-strip"
      >
        {alerts.length === 0 ? (
          <Empty
            description="No critical alerts found for the selected filters"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        ) : (
          <AlertGrid>
            {alerts.map((alert) => (
              <AlertTile
                key={alert.key}
                href={alert.href || "#"}
                $severity={alert.severity}
                data-drilldown-key={alert.drilldownKey || undefined}
                onClick={(event) => {
                  if (!alert.drilldownKey) return;
                  event.preventDefault();
                  drilldownState.openDrilldown(alert.drilldownKey);
                }}
              >
                <Space direction="vertical" size={6}>
                  <Space size={6} wrap>
                    <Tag color={alert.severity === "critical" ? "red" : "orange"}>
                      {alert.severity}
                    </Tag>
                    <Text strong>{alert.title}</Text>
                  </Space>
                  <Text>{alert.value}</Text>
                  <Text type="secondary">{alert.explanation}</Text>
                  <Text>{alert.nextAction}</Text>
                </Space>
              </AlertTile>
            ))}
          </AlertGrid>
        )}
      </AlertCard>
      <DashboardDrilldownDrawer
        open={drilldownState.open}
        onClose={drilldownState.closeDrilldown}
        drilldown={drilldownState.drilldown}
        loading={drilldownState.loading}
        error={drilldownState.error}
      />
    </>
  );
};

function buildAlerts(metricGroups) {
  const groups = metricGroups || {};
  const sales = groups.sales?.metadata || {};
  const operations = groups.operations?.metadata || {};
  const financeMetrics = groups.finance?.metrics || [];
  const alerts = [];

  addCountAlert(alerts, {
    key: "leads_waiting_beyond_sla",
    title: "Leads waiting beyond SLA",
    count: sales.leadResponse?.waitingBeyondSla,
    severity: "critical",
    explanation: "Unanswered leads have crossed the configured response threshold.",
    nextAction: "Assign follow-up ownership now.",
    drilldownKey: sales.leadResponse?.drilldowns?.waitingBeyondSla,
  });
  addCountAlert(alerts, {
    key: "qualified_without_quotes",
    title: "Qualified leads without quotes",
    count: sales.leadResponse?.qualifiedWithoutQuote,
    severity: "warning",
    explanation: "Qualified demand is not yet converted into quotes.",
    nextAction: "Prioritize quote creation.",
    drilldownKey: sales.leadResponse?.drilldowns?.qualifiedWithoutQuote,
  });

  (operations.delayIncidentAnalysis?.categories || []).forEach((category) => {
    if (!["CRITICAL", "HIGH"].includes(category.severity)) return;
    addCountAlert(alerts, {
      key: `ops_${category.key}`,
      title: category.label,
      count: category.count,
      severity: category.severity === "CRITICAL" ? "critical" : "warning",
      explanation: category.explanation,
      nextAction: category.suggestedAction,
      drilldownKey: category.drilldownKey,
      href: category.deepLink,
    });
  });

  addValueAlert(alerts, {
    key: "overdue_invoice_value",
    title: "Overdue invoice exposure",
    value: metricValue(financeMetrics, "overdue_invoice_value"),
    severity: "critical",
    explanation: "Invoices are overdue for the selected period.",
    nextAction: "Open overdue invoices and chase payment.",
    href: "/invoices?status=overdue",
  });
  addValueAlert(alerts, {
    key: "held_payouts",
    title: "Held payout exposure",
    value: metricValue(financeMetrics, "held_payouts"),
    severity: "warning",
    explanation: "Provider funds are held or at risk.",
    nextAction: "Review payout holds and resolution blockers.",
    href: "/payouts",
  });

  return alerts.sort((left, right) => severityRank(left.severity) - severityRank(right.severity));
}

function addCountAlert(alerts, config) {
  const count = Number(config.count || 0);
  if (count <= 0) return;
  alerts.push({
    ...config,
    value: `${count} ${count === 1 ? "item" : "items"}`,
  });
}

function addValueAlert(alerts, config) {
  const value = Number(config.value || 0);
  if (value <= 0) return;
  alerts.push({
    ...config,
    value: formatCurrency(value),
  });
}

function metricValue(metrics, key) {
  return metrics.find((metric) => metric.key === key)?.value;
}

function severityRank(severity) {
  return severity === "critical" ? 0 : 1;
}

function formatCurrency(value) {
  return new Intl.NumberFormat("en-ZA", {
    style: "currency",
    currency: "ZAR",
    maximumFractionDigits: 0,
  }).format(Number(value || 0));
}

export default ExecutiveRiskAlertStrip;
